import { TablePaginationConfig } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { SetURLSearchParams } from 'react-router-dom';
import { UseTableReturn } from 'type/tableType';

interface UseTableProps<T> {
  dataSource: T[];
  setSelectedList: React.Dispatch<React.SetStateAction<number[]>>;
  setSearchParams: SetURLSearchParams;
  setSelectAll: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useTable = <T>({
  dataSource,
  setSearchParams,
  setSelectAll,
  setSelectedList,
}: UseTableProps<T>): UseTableReturn<T> => {
  const onPageChange = async (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[]
  ): Promise<void> => {
    const { current, pageSize } = pagination;
    const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter;

    // 모든 파라미터를 한 번에 업데이트하여 동기화 문제 방지
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      // 정렬 파라미터
      if (field) {
        newParams.set('order_by', field.toString() || 'id');
      } else {
        newParams.delete('order_by');
      }

      if (order) {
        newParams.set('order_direction', order === 'ascend' ? 'asc' : 'desc');
      } else {
        newParams.delete('order_direction');
      }

      // 필터 파라미터
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value) {
            newParams.set(key, value?.toString() || '');
          } else {
            newParams.delete(key);
          }
        });
      }

      // 페이지네이션 파라미터
      newParams.set('page', current?.toString() || '1');
      newParams.set('page_size', pageSize?.toString() || '10');

      return newParams;
    });
  };

  const handleSelectAll = (e: CheckboxChangeEvent): void => {
    const isChecked = e.target.checked;
    if (isChecked) {
      setSelectedList(dataSource.map(item => (item as { id: number }).id));
      setSelectAll(true);
    } else {
      setSelectedList([]);
      setSelectAll(false);
    }
  };

  const handleSelect = (id: number): void => {
    setSelectedList(prevSelectedList =>
      prevSelectedList.includes(id)
        ? prevSelectedList.filter(existingId => existingId !== id)
        : [...prevSelectedList, id]
    );
  };

  return {
    onPageChange,
    handleSelectAll,
    handleSelect,
  };
};
